import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AssessmentModalShell from "../../Components/Shared/AssessmentModalShell";
import PremiumDatePicker from "../../Components/Shared/DatePicker";
import { todayLocal } from "../../utils/dateOnly";
import useUser from "../../Core/hook/useUser";
import { getAvatar } from "../../utils/avatar";
import {
  useGetAssessmentCascadeGradesQuery,
  useGetAssessmentCascadeSubjectsQuery,
  useGetAssessmentCascadeStudentsQuery,
  useCreateAssessmentGroupMutation,
} from "../../Api/attoDeskApi";

// ═══════════════════════════════════════════════════════════════════════
// Two-step Create Assessment wizard.
//
// Step 1 — Who and what: Staff → Grade → Subject → Students, each field
// clearing everything below it on change. Populated from the cascade
// endpoints (Batch 1). Multi-select student picker.
//
// Step 2 — Details: Title, Date, Due date, Type, Focus, Worth, Pass
// threshold, Instructions. NO mark. NO percentage readout. NO comments.
//
// Submits to POST /api/assessments/groups — never the legacy single-row
// endpoint. On success, shows the server's message and calls onCreated.
//
// Do not extend this wizard with mark/comment fields — those belong on
// the grading roster page, which is a separate screen.
// ═══════════════════════════════════════════════════════════════════════

export interface StaffOption { id: number; name: string; image?: string | null; role?: string | null }

// ─── Shared visual tokens for Step-2 inputs ──────────────────────────
// Every text/number/textarea/date/dropdown control in the wizard uses
// this palette so borders, radii, heights and focus rings stay in sync.
const ACCENT = "#7c3aed";        // purple modal accent — replaces the OS blue focus ring / selection colour
const ACCENT_SOFT = "#f5f3ff";   // selected/hover tint on picker rows
const BORDER = "#e2e8f0";
const CONTROL_HEIGHT = 42;

/** Attach to any text input/textarea to swap the browser's default focus
 *  outline for the modal's purple ring. */
const focusRingHandlers = (base: string = BORDER) => ({
  onFocus: (e: React.FocusEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.borderColor = ACCENT;
    (e.currentTarget as HTMLElement).style.boxShadow = `0 0 0 3px ${ACCENT}22`;
  },
  onBlur: (e: React.FocusEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.borderColor = base;
    (e.currentTarget as HTMLElement).style.boxShadow = "none";
  },
});

// ─── OptionPicker: the single custom dropdown driving every picker in
// this file (Staff on Step 1, Type on Step 2). Was originally the staff-
// only StaffPicker in Batch 8; generalised in Batch 9 so we do not have
// two dropdown implementations. Rows may render either an avatar (Staff)
// or an emoji icon (Type) — never both. ────────────────────────────────
export interface PickerOption {
  value: string | number;
  label: string;
  sublabel?: string;
  /** Rendered as a circular avatar via getAvatar(). */
  avatar?: { name: string; image?: string | null };
  /** Rendered as a plain emoji/icon glyph. */
  icon?: string;
}

interface OptionPickerProps {
  options: PickerOption[];
  value: string | number | "";
  onChange: (v: string | number) => void;
  placeholder?: string;
  /** Override the auto-decision to show a search box. */
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyText?: string;
  testId?: string;
}

const OptionPicker: React.FC<OptionPickerProps> = ({
  options, value, onChange,
  placeholder = "Select…",
  searchable, searchPlaceholder = "Search…",
  emptyText = "No matches.",
  testId,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      o.label.toLowerCase().includes(q) || (o.sublabel || "").toLowerCase().includes(q)
    );
  }, [options, search]);

  // Auto-show a search box only when the list is long enough to need it.
  const showSearch = searchable ?? options.length > 6;

  const selected = options.find((o) => o.value === value) || null;
  const triggerRef = useRef<HTMLDivElement>(null);

  // Outside-click closes.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Reset activeIdx / search when opening; focus search when present.
  useEffect(() => {
    if (open) {
      setSearch("");
      const i = value !== "" ? filtered.findIndex((o) => o.value === value) : 0;
      setActiveIdx(i >= 0 ? i : 0);
      if (showSearch) setTimeout(() => searchRef.current?.focus(), 0);
    } else {
      // When closing (Esc or select), return focus to the trigger so keyboard users don't lose their place.
      triggerRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pick = (o: PickerOption) => {
    onChange(o.value);
    setOpen(false);
  };

  const onTriggerKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
    }
  };

  const onListKey = (e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === "Escape") { e.preventDefault(); setOpen(false); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(filtered.length - 1, i + 1)); return; }
    if (e.key === "ArrowUp")   { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); return; }
    if (e.key === "Enter" && filtered[activeIdx]) { e.preventDefault(); pick(filtered[activeIdx]); return; }
  };

  const rowBase: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: 10,
    padding: "8px 12px", cursor: "pointer",
    borderBottom: `1px solid #f1f5f9`,
  };

  const renderRowLeading = (o: PickerOption) => {
    if (o.avatar) {
      return (
        <img
          src={getAvatar(o.avatar.name, o.avatar.image || null, 28)}
          alt="" width={28} height={28}
          style={{ borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
        />
      );
    }
    if (o.icon) {
      return <span style={{ fontSize: 18, width: 28, textAlign: "center", flexShrink: 0 }}>{o.icon}</span>;
    }
    return null;
  };

  return (
    <div ref={rootRef} style={{ position: "relative" }} onKeyDown={onListKey}>
      {/* Trigger — closed state */}
      <div
        ref={triggerRef}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKey}
        data-testid={testId}
        {...focusRingHandlers()}
        style={{
          width: "100%", padding: "8px 12px",
          border: `1.5px solid ${BORDER}`, borderRadius: 8,
          background: "#fff", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 10,
          minHeight: CONTROL_HEIGHT, boxSizing: "border-box", outline: "none",
          transition: "border-color 0.15s, box-shadow 0.15s",
        }}
      >
        {selected ? (
          <>
            {renderRowLeading(selected)}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13, color: "#1a1a2e", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{selected.label}</div>
              {selected.sublabel && (
                <div style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{selected.sublabel}</div>
              )}
            </div>
          </>
        ) : (
          <span style={{ fontSize: 13, color: "#94a3b8", flex: 1 }}>{placeholder}</span>
        )}
        <span style={{ fontSize: 10, color: "#94a3b8", flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>▼</span>
      </div>

      {/* Open state */}
      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 20,
            background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 10,
            boxShadow: "0 12px 32px rgba(15,23,42,0.14)", overflow: "hidden",
          }}
        >
          {showSearch && (
            <div style={{ padding: 8, borderBottom: `1px solid #f1f5f9` }}>
              <input
                ref={searchRef}
                type="search"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setActiveIdx(0); }}
                {...focusRingHandlers()}
                style={{
                  width: "100%", padding: "8px 10px", border: `1px solid ${BORDER}`,
                  borderRadius: 6, fontSize: 13, outline: "none", boxSizing: "border-box",
                  transition: "border-color 0.15s, box-shadow 0.15s",
                }}
              />
            </div>
          )}
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 16, fontSize: 12, color: "#94a3b8", textAlign: "center" }}>
                {emptyText}
              </div>
            ) : filtered.map((o, i) => {
              const isSelected = o.value === value;
              const isActive = i === activeIdx;
              return (
                <div
                  key={String(o.value)}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => pick(o)}
                  style={{
                    ...rowBase,
                    background: isSelected ? ACCENT_SOFT : isActive ? "#f8fafc" : "#fff",
                    color: isSelected ? ACCENT : "#1a1a2e",
                  }}
                >
                  {renderRowLeading(o)}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: isSelected ? ACCENT : "#1a1a2e" }}>{o.label}</div>
                    {o.sublabel && (
                      <div style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.sublabel}</div>
                    )}
                  </div>
                  {isSelected && <span style={{ color: ACCENT, fontSize: 14, fontWeight: 800 }}>✓</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export type AssessmentType = "test" | "quiz" | "homework" | "observation";

// ─── AssessmentType metadata used by the Type dropdown ──────────────
const TYPE_OPTIONS: { value: AssessmentType; label: string; icon: string }[] = [
  { value: "test",        label: "Test",        icon: "📝" },
  { value: "quiz",        label: "Quiz",        icon: "❓" },
  { value: "homework",    label: "Homework",    icon: "📚" },
  { value: "observation", label: "Observation", icon: "👀" },
];
const NUMERIC_TYPES: AssessmentType[] = ["test", "quiz", "homework"];
export const isNumericType = (t: AssessmentType) => NUMERIC_TYPES.includes(t);

export interface Step2Fields {
  title: string;
  date: string;      // YYYY-MM-DD (local, timezone-safe)
  due_date: string;  // YYYY-MM-DD or ""
  assessment_type: AssessmentType;
  focus: string;
  worth: number;
  pass_threshold: number;
  instructions: string;
}

/** Returns a list of missing / invalid field labels for Step 2 so the
 * Create button can explain WHY it is disabled instead of failing silently. */
export const step2MissingFields = (f: Step2Fields): string[] => {
  const missing: string[] = [];
  if (!f.title.trim()) missing.push("Title");
  if (!f.date) missing.push("Date");
  if (!Number.isFinite(f.worth) || f.worth < 1) missing.push("Worth (≥ 1)");
  if (isNumericType(f.assessment_type)) {
    if (!Number.isFinite(f.pass_threshold) || f.pass_threshold < 0 || f.pass_threshold > 100) {
      missing.push("Pass threshold (0–100)");
    }
  }
  return missing;
};

export const defaultStep2 = (): Step2Fields => ({
  title: "",
  date: todayLocal(),
  due_date: "",
  assessment_type: "test",
  focus: "",
  worth: 100,
  pass_threshold: 50,
  instructions: "",
});

// Cascade record shapes — the endpoints already return normalised objects
// with `id` + `name` (+ `first_name`/`last_name`/`full_name`/`avatar` for
// students). Type them so useMemo consumers stay honest.
interface CascadeGrade   { id: number; name: string }
interface CascadeSubject { id: number; name: string; color?: string | null }
interface CascadeStudent {
  id: number;
  first_name: string; last_name: string;
  full_name: string; avatar?: string | null;
}

// ── the component ──────────────────────────────────────────────────

export interface CreateAssessmentWizardProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful POST, with the server's human message. */
  onCreated?: (message: string) => void;
  /** Staff picker options. Parent already loads useGetTeamQuery so we
   * reuse rather than re-fetching. Only used when the current user is
   * allowed to switch staff (admins). */
  staffOptions?: StaffOption[];
}

const CreateAssessmentWizard: React.FC<CreateAssessmentWizardProps> = ({
  open, onClose, onCreated, staffOptions = [],
}) => {
  // ── Current user + admin gate ────────────────────────────────────
  const { staffId: myStaffId, userHasRight } = useUser();
  // Admins have `Staff_View`; tutors don't (matches the backend's
  // DataScopeHelper::isTutor inversion). Tutors can only create for
  // themselves — the picker is disabled and shows their name only.
  const canPickStaff = userHasRight("Staff_View");

  // ── Wizard state ─────────────────────────────────────────────────
  const [step, setStep] = useState<1 | 2>(1);
  const [staffId, setStaffId] = useState<number | "">("");
  const [gradeId, setGradeId] = useState<number | "">("");
  const [subjectId, setSubjectId] = useState<number | "">("");
  const [studentIds, setStudentIds] = useState<number[]>([]);
  const [step2, setStep2] = useState<Step2Fields>(defaultStep2());

  const [studentSearch, setStudentSearch] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Guards against a double-click racing two POSTs even before RTK's
  // isLoading flag flips on the second render.
  const submittedRef = useRef(false);

  // Reset everything when the modal re-opens. The RTK skip guards below
  // ensure stale cascade queries don't repopulate after this reset.
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setStaffId(myStaffId ?? "");
    setGradeId("");
    setSubjectId("");
    setStudentIds([]);
    setStudentSearch("");
    setStep2(defaultStep2());
    setSubmitError(null);
    submittedRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, myStaffId]);

  // ── Cascade queries: RTK Query. `skip` short-circuits the request
  // until the parent field is chosen; cache invalidation is handled by
  // the shared "assessment" tag so a successful create refreshes the
  // list without a manual refetch.
  const gradesQuery = useGetAssessmentCascadeGradesQuery(
    { staff_id: staffId as number },
    { skip: !open || !staffId },
  );
  const subjectsQuery = useGetAssessmentCascadeSubjectsQuery(
    { staff_id: staffId as number, grade_id: gradeId as number },
    { skip: !open || !staffId || !gradeId },
  );
  const studentsQuery = useGetAssessmentCascadeStudentsQuery(
    { staff_id: staffId as number, grade_id: gradeId as number, subject_id: subjectId as number },
    { skip: !open || !staffId || !gradeId || !subjectId },
  );
  const [createAssessmentGroup, createGroupState] = useCreateAssessmentGroupMutation();

  const grades: CascadeGrade[] = (gradesQuery.data?.data as CascadeGrade[]) || [];
  const subjects: CascadeSubject[] = (subjectsQuery.data?.data as CascadeSubject[]) || [];
  const students: CascadeStudent[] = (studentsQuery.data?.data as CascadeStudent[]) || [];
  const studentsMeta: string | null = (studentsQuery.data as any)?.meta?.reason ?? null;
  const loadingGrades = gradesQuery.isFetching;
  const loadingSubjects = subjectsQuery.isFetching;
  const loadingStudents = studentsQuery.isFetching;
  const submitting = createGroupState.isLoading;

  // Every dependent selection clears when its parent changes so a stale
  // cached grade / subject / student id can't survive a cascade change.
  useEffect(() => {
    setGradeId("");
    setSubjectId("");
    setStudentIds([]);
  }, [staffId]);

  useEffect(() => {
    setSubjectId("");
    setStudentIds([]);
  }, [gradeId]);

  useEffect(() => {
    setStudentIds([]);
    setStudentSearch("");
  }, [subjectId]);

  // ── Derived state ────────────────────────────────────────────────
  const staffMap = useMemo(() => {
    const m = new Map<number, StaffOption>();
    staffOptions.forEach((s) => m.set(s.id, s));
    return m;
  }, [staffOptions]);
  const currentStaffName = staffId ? (staffMap.get(Number(staffId))?.name ?? `Staff #${staffId}`) : "";

  const filteredStudents = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => s.full_name.toLowerCase().includes(q));
  }, [students, studentSearch]);
  const allEligibleIds = useMemo(() => students.map((s) => s.id), [students]);
  const allSelected = studentIds.length > 0 && studentIds.length === allEligibleIds.length;

  const toggleStudent = (id: number) => {
    setStudentIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  };
  const selectAll = () => setStudentIds(allEligibleIds);
  const clearAll = () => setStudentIds([]);

  const step1Complete = !!staffId && !!gradeId && !!subjectId && studentIds.length > 0;
  const step2Missing = useMemo(() => step2MissingFields(step2), [step2]);
  const step2Complete = step2Missing.length === 0;

  // ── Submit ───────────────────────────────────────────────────────
  const submit = useCallback(async () => {
    if (submittedRef.current || submitting) return;
    if (!step1Complete || !step2Complete) return;
    submittedRef.current = true;
    setSubmitError(null);
    try {
      const body = {
        staff_id: Number(staffId),
        grade_id: Number(gradeId),
        subject_id: Number(subjectId),
        student_ids: studentIds,
        title: step2.title.trim(),
        assessment_type: step2.assessment_type,
        date: step2.date,
        due_date: step2.due_date || null,
        worth: Number(step2.worth),
        pass_threshold: isNumericType(step2.assessment_type)
          ? Number(step2.pass_threshold) : null,
        focus: step2.focus.trim() || null,
        instructions: step2.instructions.trim() || null,
      };
      const res = await createAssessmentGroup(body).unwrap();
      // The mutation invalidates the shared "assessment" tag → every
      // consumer (records table, stats card, per-student performance)
      // refetches automatically; the parent's onCreated callback shows
      // the toast copy the server returned.
      onCreated?.(res?.message || "Assessment created.");
      onClose();
    } catch (e: any) {
      submittedRef.current = false;
      // RTK Query surfaces server messages under .data.message.
      const msg = e?.data?.message || e?.error || e?.message || "Failed to create assessment.";
      setSubmitError(msg);
    }
  }, [
    submitting, step1Complete, step2Complete,
    staffId, gradeId, subjectId, studentIds, step2,
    createAssessmentGroup, onCreated, onClose,
  ]);

  // ── Header + save button label per step ──────────────────────────
  const eyebrow = "New Assessment";
  const title = step === 1 ? "📝 Who and what" : "📝 Assessment details";
  const saveLabel = step === 1
    ? "Next →"
    : (submitting ? "⏳ Creating…" : "✅ Create Assessment");
  const saveDisabled = step === 1
    ? !step1Complete
    : (!step2Complete || submitting);
  const onSave = step === 1 ? () => setStep(2) : submit;

  // ── Styles ───────────────────────────────────────────────────────
  const label: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6, display: "block" };
  // Shared input token — all text/number/textarea/dropdown-trigger controls
  // in Step 2 share these values so borders, radii and heights line up.
  // Focus ring is applied imperatively via focusRingHandlers() so we don't
  // need a stylesheet just for :focus.
  const input: React.CSSProperties = {
    width: "100%", height: CONTROL_HEIGHT, padding: "0 12px",
    border: `1.5px solid ${BORDER}`, borderRadius: 8,
    fontSize: 13, background: "#fff", outline: "none",
    boxSizing: "border-box", color: "#1a1a2e",
    fontFamily: "inherit",
    transition: "border-color 0.15s, box-shadow 0.15s",
  };
  // Textareas can't have a fixed height; drop it and let `rows` size them.
  const textareaStyle: React.CSSProperties = {
    ...input, height: "auto", padding: "10px 12px",
    resize: "vertical", fontFamily: "inherit", lineHeight: 1.5,
  };
  const chip = (active: boolean): React.CSSProperties => ({
    padding: "8px 14px", borderRadius: 999,
    border: active ? "1px solid #1b3a6b" : "1px solid #e2e8f0",
    background: active ? "#1b3a6b" : "#fff",
    color: active ? "#fff" : "#334155",
    fontSize: 13, fontWeight: 600, cursor: "pointer", userSelect: "none",
  });

  // ── Progress indicator ───────────────────────────────────────────
  const StepPill: React.FC<{ n: 1 | 2; label: string }> = ({ n, label }) => {
    const active = step === n;
    const done = step > n;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 14,
          background: done ? "#16a34a" : active ? "#1b3a6b" : "#e2e8f0",
          color: done || active ? "#fff" : "#94a3b8",
          fontWeight: 800, fontSize: 12,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>{done ? "✓" : n}</div>
        <span style={{ fontSize: 13, fontWeight: 600, color: active || done ? "#1b3a6b" : "#94a3b8" }}>{label}</span>
      </div>
    );
  };

  return (
    <AssessmentModalShell
      open={open}
      onClose={onClose}
      editing={false}
      eyebrow={eyebrow}
      title={title}
      saveLabel={saveLabel}
      onSave={onSave}
      saveDisabled={saveDisabled}
      saving={submitting}
    >
      {/* Progress indicator + Back button */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <StepPill n={1} label="Who and what" />
          <div style={{ width: 32, height: 2, background: step > 1 ? "#16a34a" : "#e2e8f0" }} />
          <StepPill n={2} label="Details" />
        </div>
        {step === 2 && (
          <button
            type="button"
            onClick={() => setStep(1)}
            style={{
              background: "transparent", border: "1px solid #e2e8f0",
              padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
              color: "#64748b", cursor: "pointer",
            }}
          >← Back</button>
        )}
      </div>

      {/* ── STEP 1: Who and what ─────────────────────────────────── */}
      {step === 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* 1. Staff */}
          <div>
            <label style={label}>Staff *</label>
            {canPickStaff ? (
              <OptionPicker
                testId="wizard-staff"
                placeholder="Select a staff member…"
                emptyText="No staff match your search."
                searchPlaceholder="Search staff…"
                value={staffId}
                onChange={(v) => setStaffId(Number(v))}
                options={staffOptions.map((s): PickerOption => ({
                  value: s.id,
                  label: s.name,
                  sublabel: s.role || undefined,
                  avatar: { name: s.name, image: s.image },
                }))}
              />
            ) : (
              <div
                style={{
                  width: "100%", padding: "8px 12px",
                  border: "1.5px solid #e2e8f0", borderRadius: 8,
                  background: "#f8fafc", display: "flex", alignItems: "center",
                  gap: 10, minHeight: 44, boxSizing: "border-box",
                }}
                data-testid="wizard-staff"
              >
                {(() => {
                  const me = staffOptions.find((s) => s.id === Number(staffId));
                  return (
                    <>
                      <img
                        src={getAvatar(me?.name || currentStaffName || "You", me?.image || null, 28)}
                        alt="" width={28} height={28}
                        style={{ borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                      />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13, color: "#334155", fontWeight: 600 }}>
                          {me?.name || currentStaffName || "You"}
                        </div>
                        {me?.role && (
                          <div style={{ fontSize: 11, color: "#94a3b8" }}>{me.role}</div>
                        )}
                      </div>
                      <span style={{ color: "#94a3b8", fontSize: 11 }}>🔒 locked</span>
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          {/* 2. Grade */}
          <div>
            <label style={label}>Grade *</label>
            {!staffId ? (
              <div style={{ fontSize: 12, color: "#94a3b8" }}>Choose a staff member to see their teaching grades.</div>
            ) : loadingGrades ? (
              <div style={{ fontSize: 12, color: "#94a3b8" }}>Loading grades…</div>
            ) : grades.length === 0 ? (
              <div style={{ fontSize: 12, color: "#94a3b8" }}>No grades are assigned to this staff member.</div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {grades.map((g) => (
                  <button key={g.id} type="button" onClick={() => setGradeId(g.id)} style={chip(gradeId === g.id)}>
                    {g.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 3. Subject (SINGLE-select) */}
          <div>
            <label style={label}>Subject *</label>
            {!gradeId ? (
              <div style={{ fontSize: 12, color: "#94a3b8" }}>Choose a grade to see the subjects this staff member teaches at that grade.</div>
            ) : loadingSubjects ? (
              <div style={{ fontSize: 12, color: "#94a3b8" }}>Loading subjects…</div>
            ) : subjects.length === 0 ? (
              <div style={{ fontSize: 12, color: "#94a3b8" }}>
                No subjects are assigned to this staff member for the selected grade.
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {subjects.map((s) => (
                  <button key={s.id} type="button" onClick={() => setSubjectId(s.id)} style={chip(subjectId === s.id)}>
                    {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 4. Students (MULTI-select) */}
          <div>
            <label style={label}>
              Students *
              {studentIds.length > 0 && (
                <span style={{ marginLeft: 8, color: "#1b3a6b", fontWeight: 700 }}>
                  {studentIds.length} selected
                </span>
              )}
            </label>
            {!subjectId ? (
              <div style={{ fontSize: 12, color: "#94a3b8" }}>Choose a subject to see enrolled students.</div>
            ) : loadingStudents ? (
              <div style={{ fontSize: 12, color: "#94a3b8" }}>Loading students…</div>
            ) : students.length === 0 ? (
              <div style={{ fontSize: 12, color: "#94a3b8" }}>
                {studentsMeta === "staff_not_teaching"
                  ? "This staff member does not teach the selected subject/grade combination."
                  : studentsMeta === "no_subject_grade_pair"
                  ? "The selected grade and subject are not configured together."
                  : "No students are enrolled in this subject/grade."}
              </div>
            ) : (
              <div>
                {/* Chips of currently-selected students, removable */}
                {studentIds.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                    {studentIds.map((sid) => {
                      const s = students.find((x) => x.id === sid);
                      if (!s) return null;
                      return (
                        <span key={sid} style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          background: "#eff6ff", color: "#1b3a6b",
                          border: "1px solid #bfdbfe", borderRadius: 999,
                          padding: "4px 10px 4px 4px", fontSize: 12, fontWeight: 600,
                        }}>
                          <img
                            src={getAvatar(s.full_name, s.avatar || null, 20)}
                            alt="" width={20} height={20}
                            style={{ borderRadius: "50%", objectFit: "cover" }}
                          />
                          {s.full_name}
                          <button
                            type="button"
                            onClick={() => toggleStudent(sid)}
                            style={{ background: "transparent", border: "none", color: "#1b3a6b", cursor: "pointer", fontSize: 14, padding: 0, lineHeight: 1 }}
                            aria-label={`Remove ${s.full_name}`}
                          >×</button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Search + bulk actions */}
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <input
                    type="search"
                    placeholder="Search students…"
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    style={{ ...input, flex: 1 }}
                    data-testid="wizard-student-search"
                  />
                  <button
                    type="button"
                    onClick={allSelected ? clearAll : selectAll}
                    style={{
                      padding: "8px 14px", borderRadius: 8, border: "1px solid #e2e8f0",
                      background: "#fff", color: "#1b3a6b", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
                    }}
                    data-testid="wizard-select-all"
                  >
                    {allSelected ? "Clear all" : `Select all (${allEligibleIds.length})`}
                  </button>
                </div>

                {/* Checkbox list */}
                <div style={{
                  maxHeight: 240, overflowY: "auto",
                  border: "1px solid #e2e8f0", borderRadius: 8,
                  background: "#fff",
                }}>
                  {filteredStudents.length === 0 ? (
                    <div style={{ padding: 16, fontSize: 12, color: "#94a3b8", textAlign: "center" }}>
                      No students match your search.
                    </div>
                  ) : filteredStudents.map((s) => {
                    const checked = studentIds.includes(s.id);
                    return (
                      <label key={s.id} style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "8px 12px", cursor: "pointer",
                        background: checked ? "#f0f9ff" : "transparent",
                        borderBottom: "1px solid #f1f5f9",
                      }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleStudent(s.id)}
                        />
                        <img
                          src={getAvatar(s.full_name, s.avatar || null, 28)}
                          alt="" width={28} height={28}
                          style={{ borderRadius: "50%", objectFit: "cover" }}
                        />
                        <span style={{ fontSize: 13, color: "#1a1a2e" }}>{s.full_name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── STEP 2: Details ──────────────────────────────────────── */}
      {step === 2 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Recap chip so teachers can confirm before submit */}
          <div style={{
            display: "flex", flexWrap: "wrap", gap: 8, padding: "10px 12px",
            background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8,
            fontSize: 12, color: "#334155",
          }}>
            <span><strong>Staff:</strong> {currentStaffName || "—"}</span>
            <span>·</span>
            <span><strong>Grade:</strong> {grades.find((g) => g.id === gradeId)?.name || "—"}</span>
            <span>·</span>
            <span><strong>Subject:</strong> {subjects.find((s) => s.id === subjectId)?.name || "—"}</span>
            <span>·</span>
            <span><strong>{studentIds.length}</strong> student{studentIds.length === 1 ? "" : "s"}</span>
          </div>

          {/* Title + Type row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 200px", gap: 12 }}>
            <div>
              <label style={label}>Title *</label>
              <input
                value={step2.title}
                onChange={(e) => setStep2((p) => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Mid-term Reading Check"
                style={input}
                {...focusRingHandlers()}
                data-testid="wizard-title"
              />
            </div>
            <div>
              <label style={label}>Type *</label>
              <OptionPicker
                testId="wizard-type"
                value={step2.assessment_type}
                onChange={(v) => setStep2((p) => ({ ...p, assessment_type: v as AssessmentType }))}
                searchable={false}
                options={TYPE_OPTIONS.map((t): PickerOption => ({
                  value: t.value, label: t.label, icon: t.icon,
                }))}
              />
            </div>
          </div>

          {/* Date + Due date row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={label}>Date *</label>
              <PremiumDatePicker
                value={step2.date}
                onChange={(v) => setStep2((p) => ({ ...p, date: v }))}
              />
            </div>
            <div>
              <label style={label}>Due date</label>
              <PremiumDatePicker
                value={step2.due_date}
                onChange={(v) => setStep2((p) => ({ ...p, due_date: v }))}
                placeholder="Optional"
              />
            </div>
          </div>

          {/* Focus + Worth + Threshold row */}
          <div style={{
            display: "grid",
            gridTemplateColumns: isNumericType(step2.assessment_type) ? "1fr 120px 160px" : "1fr 120px",
            gap: 12,
          }}>
            <div>
              <label style={label}>Focus</label>
              <input
                value={step2.focus}
                onChange={(e) => setStep2((p) => ({ ...p, focus: e.target.value }))}
                placeholder="e.g. Reading"
                style={input}
                {...focusRingHandlers()}
              />
            </div>
            <div>
              <label style={label}>Worth *</label>
              <input
                type="number"
                min={1}
                value={Number.isFinite(step2.worth) ? step2.worth : ""}
                onChange={(e) => setStep2((p) => ({ ...p, worth: Number(e.target.value) }))}
                style={input}
                {...focusRingHandlers()}
              />
            </div>
            {isNumericType(step2.assessment_type) && (
              <div>
                <label style={label}>Pass threshold *</label>
                {/* % suffix sits flush inside the field so the control lines
                    up with Worth/Focus on the same baseline. */}
                <div style={{ position: "relative" }}>
                  <input
                    type="number"
                    min={0} max={100}
                    value={Number.isFinite(step2.pass_threshold) ? step2.pass_threshold : ""}
                    onChange={(e) => setStep2((p) => ({ ...p, pass_threshold: Number(e.target.value) }))}
                    style={{ ...input, paddingRight: 30 }}
                    {...focusRingHandlers()}
                  />
                  <span style={{
                    position: "absolute", right: 12, top: 0, bottom: 0,
                    display: "flex", alignItems: "center",
                    fontSize: 13, color: "#64748b", pointerEvents: "none",
                  }}>%</span>
                </div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                  A score at or above this counts as a pass.
                </div>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div>
            <label style={label}>Instructions <span style={{ color: "#94a3b8", fontWeight: 500 }}>(optional)</span></label>
            <textarea
              value={step2.instructions}
              onChange={(e) => setStep2((p) => ({ ...p, instructions: e.target.value }))}
              rows={3}
              placeholder="Any notes teachers or students should see before starting."
              style={textareaStyle}
              {...focusRingHandlers()}
            />
          </div>

          {/* Missing-field explainer + submit error */}
          {!step2Complete && (
            <div style={{
              fontSize: 12, color: "#b45309", background: "#fffbeb",
              border: "1px solid #fde68a", borderRadius: 8, padding: "8px 12px",
            }}>
              To create, complete: {step2Missing.join(", ")}.
            </div>
          )}
          {submitError && (
            <div style={{
              fontSize: 12, color: "#b91c1c", background: "#fef2f2",
              border: "1px solid #fecaca", borderRadius: 8, padding: "8px 12px",
            }}>
              {submitError}
            </div>
          )}
        </div>
      )}
    </AssessmentModalShell>
  );
};

export default CreateAssessmentWizard;
